import { css, cx, type RecipeVariantProps, sva } from "styled-system/css";

type SpinnerVariants = NonNullable<RecipeVariantProps<typeof spinnerStyles>>;

export interface SpinnerProps {
  /** Size of the spinner */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Color variant of the spinner */
  variant?: SpinnerVariants["variant"];
  /** Class name to add styles to the `svg` element of the spinner */
  className?: string;
}

/** A widget for indicating a loading state. Usually placed inside interactive elements like `Button`. */
export function Spinner({ variant = "primary", size = "md", className }: SpinnerProps) {
  const sizeProperties = spinnerProperties[size]; // Determines the rendered size of the spinner
  const properties = spinnerProperties.lg; // Determines the thickness of the spinner
  const styles = spinnerStyles({ variant });

  return (
    <svg
      width={sizeProperties.size}
      height={sizeProperties.size}
      viewBox={`0 0 ${properties.size} ${properties.size}`}
      fill="none"
      className={cx(css({ animationStyle: "spin" }), className)}
    >
      <circle
        cx={properties.size / 2}
        cy={properties.size / 2}
        r={properties.radius}
        strokeWidth="3"
        className={styles.track}
      />
      <path d={properties.pathData} fill="none" strokeWidth="3" className={styles.arc} />
    </svg>
  );
}

const spinnerStyles = sva({
  slots: ["arc", "track"],
  base: { arc: {}, track: {} },
  variants: {
    variant: {
      primary: {
        arc: { stroke: "spinner.primary.arc" },
        track: { stroke: "spinner.primary.track" },
      },
      secondary: {
        arc: { stroke: "spinner.secondary.arc" },
        track: { stroke: "spinner.secondary.track" },
      },
      brand: {
        arc: { stroke: "spinner.primary.arc" },
        track: { stroke: "spinner.primary.track" },
      },
      destructive: {
        arc: { stroke: "spinner.destructive.arc" },
        track: { stroke: "spinner.destructive.track" },
      },
      onPrimary: {
        arc: { stroke: "spinner.onPrimary.arc" },
        track: { stroke: "spinner.onPrimary.track" },
      },
    },
  },
});

const spinnerProperties: Record<
  NonNullable<SpinnerProps["size"]>,
  { size: number; radius: number; pathData: string }
> = {
  xs: {
    size: 12,
    radius: 4.5,
    pathData: createArc({ x: 6, y: 6, r: 4.5, startAngle: 0, endAngle: 90 }),
  },
  sm: {
    size: 16,
    radius: 6.5,
    pathData: createArc({ x: 8, y: 8, r: 6.5, startAngle: 0, endAngle: 90 }),
  },
  md: {
    size: 20,
    radius: 8.5,
    pathData: createArc({ x: 10, y: 10, r: 8.5, startAngle: 0, endAngle: 90 }),
  },
  lg: {
    size: 28,
    radius: 12.5,
    pathData: createArc({ x: 14, y: 14, r: 12.5, startAngle: 0, endAngle: 90 }),
  },
  xl: {
    size: 36,
    radius: 16.5,
    pathData: createArc({ x: 18, y: 18, r: 16.5, startAngle: 0, endAngle: 90 }),
  },
};

function createArc(args: Record<"x" | "y" | "r" | "startAngle" | "endAngle", number>) {
  const { x, y, r, startAngle, endAngle } = args;

  const start = polarToCartesian({ x, y, r, angle: endAngle });
  const end = polarToCartesian({ x, y, r, angle: startAngle });

  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  const pathData = ["M", start.x, start.y, "A", r, r, 0, largeArcFlag, 0, end.x, end.y].join(" ");

  return pathData;
}

function polarToCartesian(args: Record<"x" | "y" | "r" | "angle", number>) {
  const angleInRadians = ((args.angle - 90) * Math.PI) / 180;

  return {
    x: args.x + args.r * Math.cos(angleInRadians),
    y: args.y + args.r * Math.sin(angleInRadians),
  };
}
